// Les huit événements du site, un par fonction. Les composants n'importent que
// ce fichier : ils ignorent la mécanique de chargement, et un changement
// d'outil de mesure ne toucherait aucun d'eux.
//
// Les noms sont en snake_case anglais et ne se traduisent pas : ils sont ce
// qu'on lit dans l'interface PostHog et ce que documente docs/posthog.md. Les
// VALEURS de propriétés, elles, sont écrites pour être lues par un humain dans
// un entonnoir — d'où le nom de la famille de soins, et non son identifiant.
import { track } from './analytics'

/**
 * Clic sur un bouton « Réserver en ligne ».
 * `emplacement` : header · accueil_hero · accueil_milieu · accueil_bas ·
 * offres_souscrire · services_impasse · erreur_404
 *
 * `offres_souscrire` s'appelait `offres_pass` tant que la carte d'un pass menait
 * à /reserver-en-ligne. Depuis la refonte de la page Offres, elle mène à l'ancre
 * « souscrire » de sa propre page, et le seul chemin restant vers la réservation
 * est le lien de ce panneau-là : le nom aurait désigné, dans un entonnoir, une
 * carte qui ne mène plus à la réservation.
 *
 * `erreur_404` est le seul emplacement dont le clic part d'une visiteuse
 * égarée : c'est le bouton du panneau de la page introuvable. Le bouton voisin
 * mène au catalogue et n'est pas instrumenté — ce n'est pas un départ en
 * réservation.
 */
export function bookingCtaClicked(emplacement) {
  track('booking_cta_clicked', { emplacement })
}

/**
 * Le module Planity s'est effectivement chargé — posé sur le `onload` du
 * script, pas sur son injection. L'injection dit « on a essayé » ; c'est le
 * `onload` qui répond à « combien ouvrent réellement le module ».
 */
export function planityWidgetLoaded() {
  track('planity_widget_loaded')
}

/** Départ vers la page publique planity.com plutôt que le module intégré. */
export function planityExternalLinkClicked() {
  track('planity_external_link_clicked')
}

/**
 * Clic sur un lien menant à la carte cadeau.
 * `emplacement` : offres_bouton · offres_visuel · reserver_lien
 *
 * L'événement part des LIENS, pas du montage de /offrir : instrumenter les
 * deux compterait double pour qui clique. Le total des accès à la page se lit
 * donc sur son `$pageview`, accès directs et favoris compris — à savoir au
 * moment de construire l'entonnoir.
 */
export function giftCardOpened(emplacement) {
  track('gift_card_opened', { emplacement })
}

/**
 * Ouverture d'une famille de soins.
 * `famille` : le nom affiché, pas l'identifiant.
 * `origine` : grille · relance_recherche · adresse
 *
 * **L'événement PEUT partir d'une arrivée**, ce qui n'a pas toujours été vrai :
 * l'univers ouvert vit dans l'adresse (`/services?univers=<id>`), et la page
 * introuvable y envoie. Une famille s'ouvre donc sans traverser aucun
 * gestionnaire de clic. Une arrivée par lien partagé est un choix, pas un
 * chargement neutre — d'où `adresse`, distincte de `grille`.
 *
 * Ce qui reste vrai : une arrivée sur /services SANS univers dans l'adresse
 * n'émet rien. La famille affichée par défaut n'a été demandée par personne,
 * elle n'est qu'un repli d'affichage.
 */
export function serviceFamilyOpened(famille, origine) {
  track('service_family_opened', { famille, origine })
}

/**
 * Clic sur un lien téléphone. Huit origines : le pied de page, l'impasse de
 * recherche des Services (écran « aucun résultat »), le bandeau de
 * `/reserver-en-ligne`, le repli `PlanityWidgetUnavailable` affiché quand le
 * module Planity ne charge pas, les DEUX liens de la page introuvable (le
 * numéro discret du pied, rendu sur toutes les 404, et le téléphone mis en
 * avant de `/team`), le panneau « souscrire » de la page Offres — où la
 * souscription en ligne n'existe pas, ce qui fait du téléphone la voie la plus
 * directe vers un pass — et le pied de `/offrir`.
 *
 * Pas de propriété `emplacement` : les huit origines sont confondues dans la
 * mesure. C'est un choix, pas un oubli — et sa conséquence grandit à chaque
 * origine ajoutée, ce qui est assumé.
 */
export function phoneClicked() {
  track('phone_clicked')
}

/** Clic sur l'adresse du pied de page, qui ouvre l'itinéraire. */
export function itineraryClicked() {
  track('itinerary_clicked')
}

/** Clic sur l'adresse e-mail du pied de page. */
export function emailClicked() {
  track('email_clicked')
}
